// src/encounter/services/use-submitted-questionnaire.ts
// GET /api/QuestionnaireResponses/ForPatient?appointmentId={id} → the questionnaire the patient
// submitted at booking (PROD-1251). Flutter: FetchFormResponseEvent → getFormResponse
// (encounter_data_source_impl.dart:48-66). Read-only; AbortSignal forwarded. Returns null when
// no QR exists (the mapper also gates on questionnaire.questionnaireid).
//
// A 404 IS "none", not a failure. The PM API answers `NotFound()` when the appointment has no
// questionnaire response (Scrips.PracticeManagement QuestionnaireResponsesController
// `GetQuestionnaireResponse`: `if (model == null) return NotFound();`). Treating it as an error
// put "Couldn't load the submitted questionnaire." under the Chief Complaint card for every
// patient who simply had no form, after the client's default three retries (Samer, 1 Oct 2026:
// "Fix the 404 response handler … to prevent persistent error toasts"). Any other failure (5xx,
// network) still rejects, so the section keeps its honest error note.
import { useQuery } from '@tanstack/react-query';
import { ApiError, get } from '@/shared/api/api-client';
import type { SubmittedQuestionnaire } from '@/types/questionnaire-response-api';

export function useSubmittedQuestionnaire(appointmentId: string | null | undefined) {
  return useQuery<SubmittedQuestionnaire | null>({
    queryKey: ['submitted-questionnaire', appointmentId],
    enabled: !!appointmentId,
    queryFn: async ({ signal }) => {
      try {
        return await get<SubmittedQuestionnaire>(
          `/api/QuestionnaireResponses/ForPatient?appointmentId=${encodeURIComponent(appointmentId as string)}`,
          { signal },
        );
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });
}
